// src/pages/cash_management/TransactionHistoryTable.jsx
import { Fragment, useMemo, useState } from "react";

import { StatusBadge } from "@common";
import { formatMoney } from "@utils";
import { fmtDate } from "./format";

const EVENT_LABEL = {
  created: "Created (pending)",
  confirmed: "Confirmed",
  rejected: "Rejected",
  voided: "Voided",
  edited: "Edited",
  comment: "Comment",
};

const SOURCE_LABEL = {
  clinic: "Clinic balance",
  personal: "Personal cash on hand",
};

/**
 * Renders one row per transaction with an expand toggle that reveals the
 * full event timeline (created → confirmed / rejected / voided …).
 */
export default function TransactionHistoryTable({ rows }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ad = new Date(a.occurred_on || a.created_at || 0);
      const bd = new Date(b.occurred_on || b.created_at || 0);
      return bd - ad;
    });
  }, [rows]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-palette-gray-soft text-left text-xs uppercase text-palette-gray-secondary">
          <tr>
            <th className="w-8 px-3 py-2" aria-label="Expand" />
            <th className="px-3 py-2">Transaction Date</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Source</th>
            <th className="px-3 py-2">Giver</th>
            <th className="px-3 py-2">Receiver</th>
            <th className="px-3 py-2">Amount</th>
            <th className="px-3 py-2">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-palette-gray-subtle">
          {sortedRows.map((tx) => {
            const isOpen = expanded.has(tx.id);
            const sourceKey = tx.source || "clinic";
            const events =
              Array.isArray(tx.events) && tx.events.length > 0
                ? [...tx.events].sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0))
                : [
                    {
                      id: `synthetic-${tx.id}`,
                      type: "created",
                      actor_email: tx.requested_by_email || tx.giver_email,
                      at: tx.created_at,
                      note: tx.note || "",
                    },
                  ];
            return (
              <Fragment key={tx.id}>
                <tr
                  className="cursor-pointer hover:bg-palette-gray-soft"
                  onClick={() => toggle(tx.id)}
                >
                  <td className="px-3 py-2 text-palette-gray-secondary">
                    <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                  </td>
                  <td className="px-3 py-2 text-palette-gray-badge">
                    {tx.occurred_on || fmtDate(tx.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={tx.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-palette-gray-badge">
                    {SOURCE_LABEL[sourceKey] || sourceKey}
                  </td>
                  <td className="px-3 py-2">{tx.giver_email}</td>
                  <td className="px-3 py-2">{tx.receiver_email}</td>
                  <td className="px-3 py-2 font-mono">{formatMoney(tx.amount)}</td>
                  <td className="px-3 py-2 text-palette-gray-badge">{tx.note || "—"}</td>
                </tr>
                {isOpen && (
                  <tr className="bg-palette-gray-soft">
                    <td colSpan={9} className="px-6 py-3">
                      <div className="text-xs font-semibold uppercase text-palette-gray-secondary mb-2">
                        Timeline
                      </div>
                      <ol className="space-y-2 border-l-2 border-palette-gray-divider pl-4">
                        {events.map((ev) => (
                          <li key={ev.id} className="text-sm">
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusBadge status={ev.type === "created" ? "pending" : ev.type} />
                              <span className="font-medium text-palette-gray-heading">
                                {EVENT_LABEL[ev.type] || ev.type}
                              </span>
                              <span className="text-xs text-palette-gray-secondary">
                                {fmtDate(ev.at)}
                              </span>
                              <span className="text-xs text-palette-gray-secondary">
                                by {ev.actor_email || "—"}
                              </span>
                            </div>
                            {ev.note && (
                              <div className="mt-1 text-xs text-palette-gray-badge">
                                "{ev.note}"
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
